import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type {
    ConfigTemplateDraft,
    ConfigTemplateIdentity,
    ConfigTemplateLevel,
} from '../../types/configTemplate.types';
import { emptyDraft } from '../../pages/ConfigTemplates/utils/draftPayload';

interface ConfigTemplateDraftState {
    draft: ConfigTemplateDraft | null;
    lastSaved: string | null;
}

const initialState: ConfigTemplateDraftState = {
    draft: null,
    lastSaved: null,
};

const configTemplateDraftSlice = createSlice({
    name: 'configTemplateDraft',
    initialState,
    reducers: {
        hydrateDraft(
            state,
            action: PayloadAction<{ draft: ConfigTemplateDraft; lastSaved: string | null }>
        ) {
            state.draft = action.payload.draft;
            state.lastSaved = action.payload.lastSaved;
        },
        resetDraft(state, action: PayloadAction<ConfigTemplateLevel>) {
            state.draft = emptyDraft(action.payload);
            state.lastSaved = null;
        },
        clearDraft(state) {
            state.draft = null;
            state.lastSaved = null;
        },
        setDraftIdentity(state, action: PayloadAction<Partial<ConfigTemplateIdentity>>) {
            if (!state.draft) return;
            state.draft.identity = { ...state.draft.identity, ...action.payload };
        },
        setDraftId(state, action: PayloadAction<string>) {
            if (!state.draft) return;
            state.draft.id = action.payload;
        },
        markDraftSaved(state, action: PayloadAction<string>) {
            state.lastSaved = action.payload;
        },
        setFieldIncluded(
            state,
            action: PayloadAction<{ path: string; included: boolean; value?: unknown }>
        ) {
            if (!state.draft) return;
            const { path, included, value } = action.payload;
            const next = new Set(state.draft.included);
            if (included) {
                next.add(path);
                if (value !== undefined) state.draft.values[path] = value;
            } else {
                next.delete(path);
                delete state.draft.values[path];
            }
            state.draft.included = Array.from(next);
        },
        setFieldValue(state, action: PayloadAction<{ path: string; value: unknown }>) {
            if (!state.draft) return;
            state.draft.values[action.payload.path] = action.payload.value;
            if (!state.draft.included.includes(action.payload.path)) {
                state.draft.included = [...state.draft.included, action.payload.path];
            }
        },
        setManyIncluded(
            state,
            action: PayloadAction<{
                paths: string[];
                included: boolean;
                defaults: Record<string, unknown>;
            }>
        ) {
            if (!state.draft) return;
            const next = new Set(state.draft.included);
            for (const path of action.payload.paths) {
                if (action.payload.included) {
                    next.add(path);
                    if (!(path in state.draft.values) && path in action.payload.defaults) {
                        state.draft.values[path] = action.payload.defaults[path];
                    }
                } else {
                    next.delete(path);
                    delete state.draft.values[path];
                }
            }
            state.draft.included = Array.from(next);
        },
        replaceDraftSettings(
            state,
            action: PayloadAction<{ included: string[]; values: Record<string, unknown> }>
        ) {
            if (!state.draft) return;
            state.draft.included = action.payload.included;
            state.draft.values = action.payload.values;
        },
        applyCopySnapshot(
            state,
            action: PayloadAction<{
                included: string[];
                values: Record<string, unknown>;
                sourceName: string;
            }>
        ) {
            if (!state.draft) return;
            state.draft.included = action.payload.included;
            state.draft.values = action.payload.values;
            state.draft.copyBaseline = {
                included: action.payload.included,
                values: action.payload.values,
            };
            state.draft.copySourceName = action.payload.sourceName;
        },
    },
});

export const {
    hydrateDraft,
    resetDraft,
    clearDraft,
    setDraftIdentity,
    setDraftId,
    markDraftSaved,
    setFieldIncluded,
    setFieldValue,
    setManyIncluded,
    replaceDraftSettings,
    applyCopySnapshot,
} = configTemplateDraftSlice.actions;

export const useConfigTemplateDraft = () =>
    useSelector((state: RootState) => state.configTemplateDraft.draft);

export const useConfigTemplateDraftMeta = () =>
    useSelector((state: RootState) => ({
        draft: state.configTemplateDraft.draft,
        lastSaved: state.configTemplateDraft.lastSaved,
    }));

export default configTemplateDraftSlice.reducer;
